import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import styled from "@emotion/styled"
import { MessageBubble, WhatsAppFlowPayloadTitle } from "../util"
import _ from "lodash"
import { isJSONString } from "../../util"

// Styled components for WhatsApp Flow card - with safety measures
const FlowCard = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme?.gray1 || "#e0e0e0"};
  overflow: hidden;
  max-width: 100%;
  box-shadow: ${(props) => props.theme?.cardBoxShadow || "0px 0px 8px 0px rgba(0, 0, 0, 0.1)"};
`

const FlowCardHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${(props) => props.theme?.gray1 || "#e0e0e0"};
  background-color: ${(props) => props.theme?.gray0 || "#f8f9fa"};
  font-weight: 600;
  font-size: ${(props) => props.theme?.textSm || "14px"};
  color: ${(props) => props.theme?.gray8 || "#333"};
`

const FlowCardBody = styled.div`
  padding: 16px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
  line-height: 1.4;
  color: ${(props) => props.theme?.gray9 || "#333"};
`

const FlowCardFooter = styled.div`
  padding: 0;
  border-top: ${(props) => props.hasBody ? `1px solid ${props.theme?.gray1 || "#e0e0e0"}` : "none"};
  background-color: ${(props) => props.theme?.gray0 || "#f8f9fa"};
`

const FlowToggleButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme?.primary || "#0084FF"};
  cursor: pointer;
  font-size: ${(props) => props.theme?.textSm || "14px"};
  font-weight: 500;
  text-align: center;
  width: 100%;
  padding: 12px 16px;
  
  &:hover {
    opacity: 0.8;
  }
`

const Payload = ({ data, color, isAgent }) => {
  const { t } = useTranslation("common")
  const [expanded, setExpanded] = useState(false)

  // Detect if this is a WhatsApp Flow (nfm_reply) payload and normalize the response JSON
  const { isWhatsappFlow, flowResponsePretty } = useMemo(() => {
    try {
      // Support multiple shapes coming from different integrations
      // 1) data = { payload: { type: 'nfm_reply', nfm_reply: { name: 'flow', response_json: '{...}' } } }
      // 2) data = { subtype: 'nfm_reply', name: 'flow', payload: { ...fields } }
      // 3) data = { payload: { ...fields } } (legacy)
      let isFlow = false
      let responseObj = null

      // shape 1
      if (data?.payload?.type === "nfm_reply" || data?.payload?.nfm_reply?.name === "flow") {
        isFlow = true
        const responseJson = data?.payload?.nfm_reply?.response_json
        if (isJSONString(responseJson)) {
          responseObj = JSON.parse(responseJson)
        }
      }

      // shape 2
      if (!responseObj && (data?.subtype === "nfm_reply" && data?.name === "flow")) {
        isFlow = true
        if (_.isObject(data?.payload)) {
          responseObj = data.payload
        }
      }

      // Fallback legacy shape
      if (!responseObj && _.isObject(data?.payload) && (data?.name === "flow" || data?.payload?.flow_token)) {
        isFlow = true
        responseObj = data.payload
      }

      // Prepare a pretty string and strip outer braces when expanded rendering
      const pretty = responseObj ? JSON.stringify(responseObj, null, 2) : null
      return { isWhatsappFlow: isFlow, flowResponsePretty: pretty }
    } catch (e) {
      return { isWhatsappFlow: false, flowResponsePretty: null }
    }
  }, [data?.payload, data?.subtype, data?.name])

  // Non-flow payloads fallback to previous behavior
  let text
  if (!isWhatsappFlow) {
    if (data?.title && data?.description) {
      text = data?.title + "\n" + data?.description
    } else if (data?.title) {
      text = data.title
    } else if (data?.text) {
      text = data.text
    } else if (data?.payload) {
      if (_.isString(data.payload)) {
        text = data.payload
      } else {
        text = JSON.stringify(data.payload, null, 2)
      }
      if (data?.payload?.type === "nfm_reply" || data?.payload?.nfm_reply?.name === "flow") {
        if (isJSONString(data?.payload?.nfm_reply?.response_json)) {
          text = JSON.stringify(JSON.parse(data.payload.nfm_reply.response_json), null, 2)
        }
      }
    }
  }

  const stripBraces = (pretty) => {
    if (!pretty) return pretty
    try {
      return pretty.replace(/^\{\n?/, "").replace(/\n?\}$/, "")
    } catch (e) {
      return pretty
    }
  }

  return (
    <>
      {isWhatsappFlow ? (
        <FlowCard>
          <FlowCardHeader>
            WhatsApp Flow Response
          </FlowCardHeader>

          {expanded && flowResponsePretty && (
            <FlowCardBody>
              {stripBraces(flowResponsePretty)}
            </FlowCardBody>
          )}

          <FlowCardFooter hasBody={expanded && flowResponsePretty}>
            <FlowToggleButton
              type="button"
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
              aria-label={expanded ? "Hide WhatsApp Flow details" : "View WhatsApp Flow details"}
            >
              {expanded && flowResponsePretty ? "Hide the details" : "View the details"}
            </FlowToggleButton>
          </FlowCardFooter>
        </FlowCard>
      ) : (
        <>
          {(data?.payload?.type === "nfm_reply" || data?.payload?.nfm_reply?.name === "flow") && <WhatsAppFlowPayloadTitle>{t?.("message_types.payload.whatsapp_flow_payload")}:</WhatsAppFlowPayloadTitle>}
          <MessageBubble isAgent={isAgent} color={color} className="text">{text}</MessageBubble>
        </>
      )}
    </>
  )
}

export default Payload
